import cx from 'classnames';
import type React from 'react';

import { ArrowIcon } from '../arrow-icon';
import * as Styled from './pagination.styles';
type PageProps = {
  page: number;
  currentPage: number;
  onPageClick: (page: number) => void;
};
const Page: React.FC<PageProps> = ({ page, currentPage, onPageClick }) => {
  const isActive = page === currentPage;
  return (
    <Styled.PageItem
      className={cx({ ['activePageItem']: isActive })}
      key={page}
      onClick={() => onPageClick(page)}
    >
      {isActive ? page : <Styled.PageLink>{page}</Styled.PageLink>}
    </Styled.PageItem>
  );
};

const Ellipsis: React.FC = () => (
  <Styled.PageItem className="disabledPageItem">
    <span>...</span>
  </Styled.PageItem>
);

/*
 * Output format: [1, ..., c-1, c, c+1, ..., t]
 */
type PagesProps = {
  onPageClick: (page: number) => void;
  currentPage: number;
  totalPages: number;
};
const Pages: React.FC<PagesProps> = ({
  onPageClick,
  currentPage,
  totalPages,
}) => {
  // How many elements at each side of the currentPage
  const windowDelta = 1;
  const windowSize = 1 + windowDelta * 2;
  // The positions that maintain the number of elements displayed constant
  const rightCroppingLimit = 1 + windowSize;
  const leftCroppingLimit = totalPages - windowSize;
  // The first and last element to be rendered in the window
  const right = Math.max(currentPage, rightCroppingLimit) + windowDelta;
  const left = Math.min(currentPage, leftCroppingLimit) - windowDelta;

  // Calculate the pages that should be included by definition of the limits
  const range = [];
  range.push(1);
  if (totalPages > 1) {
    for (let i = left; i <= right; i++) {
      if (i > 1 && i < totalPages) {
        range.push(i);
      }
    }
    range.push(totalPages);
  }

  // Fill the sequence gaps with ellipses, or a page if there's only one missing
  const result = [];
  for (let i = 0; i < range.length; i++) {
    const pageValue = range[i];
    if (i > 0) {
      const previousValue = range[i - 1];
      if (pageValue - previousValue === 2) {
        result.push(
          <Page
            page={previousValue + 1}
            currentPage={currentPage}
            onPageClick={onPageClick}
            key={previousValue + 1}
          />
        );
      } else if (pageValue - previousValue > 2) {
        result.push(<Ellipsis key={previousValue + 1} />);
      }
    }
    result.push(
      <Page
        page={pageValue}
        key={pageValue}
        currentPage={currentPage}
        onPageClick={onPageClick}
      />
    );
  }

  return <>{result}</>;
};

type ChevronProps = {
  direction?: 'left' | 'right';
  targetPage?: number;
  onPageClick?: (targetPage: number) => void;
};
const Chevron: React.FC<ChevronProps> = ({
  direction,
  targetPage,
  onPageClick,
}) => {
  return (
    <Styled.PageItem
      className={cx({ disabled: !!onPageClick })}
      onClick={
        onPageClick && targetPage ? () => onPageClick(targetPage) : undefined
      }
    >
      <ArrowIcon className={'disabledArrowIcon'} direction={direction} />
    </Styled.PageItem>
  );
};

type PaginationProps = {
  /** Page that is being displayed currently */
  currentPage: number;
  /** Total number of items available */
  total: number;
  /** Items per page */
  perPage: number;
  /** Callback triggered on a page click, receives the page number as an argument */
  onPageClick: (page: number) => void;
};
export const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  total,
  perPage,
  onPageClick,
}) => {
  const totalPages = Math.ceil(total / perPage) || 1;

  return (
    <Styled.Wrapper>
      <Styled.Pagination>
        <Chevron
          direction="left"
          targetPage={currentPage - 1}
          onPageClick={currentPage > 1 ? onPageClick : undefined}
        />
        <Pages
          onPageClick={onPageClick}
          currentPage={currentPage}
          totalPages={totalPages}
        />
        <Chevron
          direction="right"
          targetPage={currentPage + 1}
          onPageClick={currentPage < totalPages ? onPageClick : undefined}
        />
      </Styled.Pagination>
    </Styled.Wrapper>
  );
};
